// Form pieces in the admin boards' look. Each field has a visible label (or an aria-label where the
// board draws a placeholder only), a hint and an error that a screen reader reads with it.
import { useId } from 'react'
import { Icon } from '../shell/Icons.jsx'
import { Dot } from './Bits.jsx'

// A label over its control, with a hint and an error:
//   <Field label="Reason" hint="Logged on their history." error={err}>
//     {props => <TextInput {...props} value={reason} onChange={setReason} />}
//   </Field>
// The child gets { id, 'aria-describedby', 'aria-invalid' } to spread on the control.
// plain: the label in ordinary type, not the boards' small capitals.
export function Field({ label, hint, error, plain = false, children, className = '' }) {
  const id = useId()
  const hintId = hint ? `${id}-hint` : undefined
  const errId = error ? `${id}-err` : undefined
  const props = { id, 'aria-describedby': [hintId, errId].filter(Boolean).join(' ') || undefined, 'aria-invalid': error ? true : undefined }
  return (
    <div className={`ad-field ${className}`.trim()}>
      {label && <label htmlFor={id} className={`ad-label${plain ? ' ad-label--plain' : ''}`}>{label}</label>}
      {typeof children === 'function' ? children(props) : children}
      {hint && <p className="ad-hint" id={hintId}>{hint}</p>}
      {error && <p className="ad-error" id={errId} role="alert">{typeof error === 'string' ? error : error.message}</p>}
    </div>
  )
}

// onChange gets the value, not the event
export function TextInput({ value, onChange, className = '', type = 'text', ...rest }) {
  return <input type={type} className={`ad-input ${className}`.trim()} value={value ?? ''} onChange={e => onChange?.(e.target.value)} {...rest} />
}

export function TextArea({ value, onChange, className = '', rows = 3, ...rest }) {
  return <textarea className={`ad-textarea ${className}`.trim()} rows={rows} value={value ?? ''} onChange={e => onChange?.(e.target.value)} {...rest} />
}

// options: [{ value, label, disabled }]; placeholder: a first option with no value
export function Select({ value, onChange, options, placeholder, className = '', ...rest }) {
  return (
    <select className={`ad-select ${className}`.trim()} value={value ?? ''} onChange={e => onChange?.(e.target.value)} {...rest}>
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {options.map(o => <option key={o.value} value={o.value} disabled={o.disabled}>{o.label}</option>)}
    </select>
  )
}

// The search box over People's list
export function SearchBox({ value, onChange, label, placeholder = label }) {
  return (
    <div className="ad-search">
      <Icon name="search" />
      <input className="ad-input" type="search" aria-label={label} placeholder={placeholder} value={value ?? ''} onChange={e => onChange?.(e.target.value)} />
    </div>
  )
}

// A tick box with its words beside it: <Checkbox checked={email} onChange={setEmail}>Email each of them ...</Checkbox>
export function Checkbox({ checked, onChange, children, disabled, className = '', ...rest }) {
  return (
    <label className={`ad-check ${className}`.trim()}>
      <input type="checkbox" checked={Boolean(checked)} disabled={disabled} onChange={e => onChange?.(e.target.checked)} {...rest} />
      <span>{children}</span>
    </label>
  )
}

// The boards' on and off switch (AdminPerson's free things, a recording's Live). label names it for a
// screen reader; stateLabel is the small word beside it (Shown, Held back).
export function Switch({ checked, onChange, label, stateLabel, disabled }) {
  return (
    <span className="ad-switch-row">
      {stateLabel && <span className="ad-switch-label" aria-hidden="true">{stateLabel}</span>}
      <button type="button" role="switch" className="ad-switch" aria-checked={Boolean(checked)} aria-label={label}
        disabled={disabled} onClick={() => onChange?.(!checked)}>
        <span className="ad-switch-knob" />
      </button>
    </span>
  )
}

// 1 of several, as the boards' option buttons draw it (the products in Grant a product, For how long).
// Nothing is picked until value matches an option. row: side by side (For how long).
//   <Options label="For how long" row value={len} onChange={setLen}
//     options={[{ value: 'same_as_buyer', label: 'Same as a buyer' }, { value: 'until_date', label: 'Until a date' }, ...]} />
// An option may carry note (the grey words on the right), product (its dot) and disabled.
export function Options({ label, labelledBy, value, onChange, options, row = false, disabled = false, className = '' }) {
  function key(e, i) {
    const enabled = options.map((o, j) => [o, j]).filter(([o]) => !o.disabled)
    if (!['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft'].includes(e.key) || !enabled.length) return
    e.preventDefault()
    const at = enabled.findIndex(([, j]) => j === i)
    const step = e.key === 'ArrowDown' || e.key === 'ArrowRight' ? 1 : -1
    const [next] = enabled[(at + step + enabled.length) % enabled.length]
    onChange?.(next.value)
    e.currentTarget.parentElement?.querySelectorAll('[role="radio"]')[options.indexOf(next)]?.focus()
  }
  const anyPicked = options.some(o => o.value === value)
  return (
    <div className={`ad-options${row ? ' ad-options--row' : ''} ${className}`.trim()} role="radiogroup" aria-label={labelledBy ? undefined : label} aria-labelledby={labelledBy}>
      {options.map((o, i) => {
        const on = o.value === value
        return (
          <button key={String(o.value)} type="button" role="radio" className="ad-option" aria-checked={on}
            // with nothing picked the first option takes Tab; after that the picked one does
            tabIndex={on || (!anyPicked && i === 0) ? 0 : -1}
            disabled={disabled || o.disabled} onClick={() => onChange?.(o.value)} onKeyDown={e => key(e, i)}>
            <span className="ad-option-name">{o.product && <Dot product={o.product} size={9} />}{o.label}</span>
            {o.note && <span className="ad-option-note">{o.note}</span>}
          </button>
        )
      })}
    </div>
  )
}

// How a grant is paid for (Adam, 4 Oct, answer 2): asked on Grant a product, Add a person and Grant in
// bulk, with nothing picked until Adam picks. The functions refuse a grant without it.
export const GRANT_SOURCES = [
  { value: 'manual', label: 'Paid another way, counts in reports' },
  { value: 'comp', label: 'Free, kept out of reports' },
]
export function SourceChoice({ value, onChange, disabled, label = 'How it was paid for' }) {
  return <Options label={label} value={value} onChange={onChange} options={GRANT_SOURCES} disabled={disabled} />
}
